/**
 * Interactive Leaflet map with polygon/rectangle drawing tools for geofencing.
 * Plots search results as color-coded markers by property type.
 */

import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import type { CompSale } from "../../api/comps";
import { fmtCurrency, fmtPSF } from "../../utils/format";

// Fix Leaflet default icon paths (broken by webpack)
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const MIAMI_CENTER: L.LatLngExpression = [25.7617, -80.1918];
const DEFAULT_ZOOM = 12;

const TYPE_COLORS: Record<string, string> = {
  Retail: "#16a34a",
  Office: "#2563eb",
  Industrial: "#d97706",
  Hospitality: "#9333ea",
  "Special Purpose": "#6b7280",
  Commercial: "#0d9488",
};

interface CompsMapProps {
  onGeofenceDrawn: (rings: number[][][]) => void;
  onGeofenceCleared: () => void;
  results: CompSale[];
  selectedParcelId: string | null;
  onMarkerClick: (parcelId: string) => void;
}

export default function CompsMap({
  onGeofenceDrawn,
  onGeofenceCleared,
  results,
  selectedParcelId,
  onMarkerClick,
}: CompsMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const drawnRef = useRef<L.FeatureGroup | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current).setView(MIAMI_CENTER, DEFAULT_ZOOM);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      edit: { featureGroup: drawnItems },
      draw: {
        polygon: { allowIntersection: false, shapeOptions: { color: "#2D5016", weight: 2 } },
        rectangle: { shapeOptions: { color: "#2D5016", weight: 2 } },
        circle: false,
        circlemarker: false,
        marker: false,
        polyline: false,
      },
    });
    map.addControl(drawControl);

    const markers = L.layerGroup().addTo(map);

    map.on(L.Draw.Event.CREATED, (e: any) => {
      drawnItems.clearLayers();
      drawnItems.addLayer(e.layer);
      const latlngs = e.layer.getLatLngs()[0] as L.LatLng[];
      // Convert Leaflet [lat, lng] to GeoJSON [lng, lat]
      const ring = latlngs.map((ll: L.LatLng) => [ll.lng, ll.lat]);
      ring.push(ring[0]); // close the ring
      onGeofenceDrawn([ring]);
    });

    map.on(L.Draw.Event.DELETED, () => {
      onGeofenceCleared();
    });

    mapRef.current = map;
    drawnRef.current = drawnItems;
    markersRef.current = markers;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update markers when results change
  useEffect(() => {
    if (!markersRef.current || !mapRef.current) return;
    markersRef.current.clearLayers();

    results.forEach((comp) => {
      const color = TYPE_COLORS[comp.property_type] || "#6b7280";
      const marker = L.circleMarker([comp.latitude, comp.longitude], {
        radius: 7,
        fillColor: color,
        color: "#fff",
        weight: 1.5,
        fillOpacity: 0.85,
      });

      marker.bindPopup(
        `<div style="font-family:Inter,sans-serif;font-size:13px;line-height:1.4">
          <strong>${comp.address || "N/A"}</strong><br/>
          ${comp.property_type} (${comp.dor_code})<br/>
          ${comp.sale_price != null ? fmtCurrency(comp.sale_price) : "Sale price: N/A"}<br/>
          ${comp.price_psf != null ? fmtPSF(comp.price_psf) : ""}<br/>
          ${comp.sale_date || ""}
        </div>`
      );

      marker.on("click", () => onMarkerClick(comp.parcel_id));
      marker.addTo(markersRef.current!);
    });
  }, [results, onMarkerClick]);

  // Pan to selected marker
  const panToParcel = useCallback(
    (parcelId: string | null) => {
      if (!parcelId || !mapRef.current) return;
      const comp = results.find((r) => r.parcel_id === parcelId);
      if (comp) {
        mapRef.current.panTo([comp.latitude, comp.longitude], { animate: true });
      }
    },
    [results]
  );

  useEffect(() => {
    panToParcel(selectedParcelId);
  }, [selectedParcelId, panToParcel]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full min-h-[400px] rounded-lg border border-grove-300"
    />
  );
}
